import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import Sidebar from "../Navigations/Sidebar";
import usePageTitle from "../../hooks/usePageTitle";
import UserApiClient from "../../services/UserApiClient";
import ResetPassword from "./ResetPassword";

const UserSettings: React.FC = () => {
  const { user, updateUserProfile } = useAuth();

  usePageTitle("User Settings");

  // Settings state
  const [settings, setSettings] = useState({
    theme: "light",
    notifications: true,
    emailUpdates: false,
    currency: "USD",
    language: "English",
    timezone: "UTC-5",
  });

  const formatPhoneNumber = (phoneNumber: string) => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, "");

    // Limit to exactly 10 digits
    const limited = cleaned.substring(0, 10);

    // Apply formatting based on length
    if (limited.length >= 7) {
      return `(${limited.substring(0, 3)}) ${limited.substring(
        3,
        6
      )}-${limited.substring(6)}`;
    } else if (limited.length >= 4) {
      return `(${limited.substring(0, 3)}) ${limited.substring(3)}`;
    } else if (limited.length >= 1) {
      return `(${limited}`;
    }

    return limited;
  };

  const [profile, setProfile] = useState({
    username: user?.username || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    phone: user?.phone ? formatPhoneNumber(user.phone) : "",
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  const handleEditProfile = async () => {
    if (!isEditing) {
      // Start editing
      setIsEditing(true);
      setError("");
    } else {
      // Save profile
      try {
        setIsSaving(true);
        setError("");

        if (!user?.id) {
          throw new Error("User ID not found");
        }

        const userData = {
          ...profile,
          phone: profile.phone.replace(/\D/g, ""), // Send only digits to API
        };

        await UserApiClient.updateUser(
          true, // isFromUserSetting as true
          user.id,
          userData
        );

        // Update the AuthContext with the new profile data
        updateUserProfile({
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          phone: profile.phone,
        });

        setIsEditing(false);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (err: any) {
        console.error("Error updating profile:", err);
        setError(err.message || "Failed to update profile");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleRefreshProfile = () => {
    // In a real app, this would fetch fresh data from the backend
    setProfile({
      username: user?.username || "",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone ? formatPhoneNumber(user.phone) : "",
    });
    setIsEditing(false);
    setError("");
  };

  const handleResetPassword = () => {
    setIsResetPasswordOpen(true);
  };

  const handleCloseResetPassword = () => {
    setIsResetPasswordOpen(false);
  };

  const handlePasswordReset = () => {
    // Show success message after password reset
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
    handleCloseResetPassword();
  };

  const handleSettingChange = (key: string, value: any) => {
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleProfileChange = (key: string, value: string) => {
    if (key === "phone") {
      // Format phone number as user types
      const formattedPhone = formatPhoneNumber(value);
      setProfile((prev) => ({
        ...prev,
        [key]: formattedPhone,
      }));
    } else {
      setProfile((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  const handleSaveSettings = () => {
    // In a real app, this would save to backend and update the AuthContext
    // For now, we'll just show success message
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <div className="app-layout">
      <Sidebar currentPage="/settings" />

      <div className="main-content">
        <div className="page-header">
          <h2>👤 User Settings</h2>
          <p>Manage your account preferences and settings</p>
        </div>
        <div className="settings-container">
          {showSuccess && (
            <div className="success-message">
              ✅ Profile updated successfully!
            </div>
          )}
          {error && <div className="error-message">❌ {error}</div>}

          <div className="settings-sections">
            {/* Profile Section */}
            <div className="settings-section">
              <div
                className="section-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h2>👤 Profile Information</h2>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    className="wide-dash-border-btn"
                    onClick={handleResetPassword}
                    title="Reset Password"
                    data-icon="🔑"
                  >
                    🔑 Reset Password
                  </button>

                  <button
                    className="wide-dash-border-btn"
                    onClick={handleEditProfile}
                    title={isEditing ? "Save Profile" : "Edit Profile"}
                    disabled={isSaving}
                    data-icon={isSaving ? "⏳" : isEditing ? "✓" : "✏️"}
                  >
                    {isSaving
                      ? "⏳ Saving..."
                      : isEditing
                      ? "✓ Save"
                      : "✏️ Edit"}
                  </button>

                  <button
                    className="wide-dash-border-btn"
                    onClick={handleRefreshProfile}
                    title="Refresh Profile"
                    data-icon="↻"
                  >
                    ↻ Refresh
                  </button>
                </div>
              </div>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Username</label>
                  <input
                    type="text"
                    value={profile.username}
                    onChange={(e) =>
                      handleProfileChange("username", e.target.value)
                    }
                    disabled
                  />
                </div>
                <div className="setting-item">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={profile.firstName}
                    onChange={(e) =>
                      handleProfileChange("firstName", e.target.value)
                    }
                    placeholder="Enter your first name"
                    disabled={!isEditing}
                  />
                </div>
                <div className="setting-item">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={profile.lastName}
                    onChange={(e) =>
                      handleProfileChange("lastName", e.target.value)
                    }
                    placeholder="Enter your last name"
                    disabled={!isEditing}
                  />
                </div>
                <div className="setting-item">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profile.email}
                    onChange={(e) =>
                      handleProfileChange("email", e.target.value)
                    }
                    placeholder="Enter your email"
                    disabled={!isEditing}
                  />
                </div>
                <div className="setting-item">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) =>
                      handleProfileChange("phone", e.target.value)
                    }
                    placeholder="(232) 323-2323"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Preferences Section */}
            <div className="settings-section" style={{ display: "none" }}>
              <h2>⚙️ Application Preferences</h2>
              <div className="settings-grid">
                <div className="setting-item">
                  <label>Theme</label>
                  <select
                    value={settings.theme}
                    onChange={(e) =>
                      handleSettingChange("theme", e.target.value)
                    }
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
                <div className="setting-item">
                  <label>Currency</label>
                  <select
                    value={settings.currency}
                    onChange={(e) =>
                      handleSettingChange("currency", e.target.value)
                    }
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="CAD">CAD (C$)</option>
                  </select>
                </div>
                <div className="setting-item">
                  <label>Language</label>
                  <select
                    value={settings.language}
                    onChange={(e) =>
                      handleSettingChange("language", e.target.value)
                    }
                  >
                    <option value="English">English</option>
                    <option value="Spanish">Spanish</option>
                    <option value="French">French</option>
                    <option value="German">German</option>
                  </select>
                </div>
                <div className="setting-item">
                  <label>Timezone</label>
                  <select
                    value={settings.timezone}
                    onChange={(e) =>
                      handleSettingChange("timezone", e.target.value)
                    }
                  >
                    <option value="UTC-8">Pacific Time (UTC-8)</option>
                    <option value="UTC-7">Mountain Time (UTC-7)</option>
                    <option value="UTC-6">Central Time (UTC-6)</option>
                    <option value="UTC-5">Eastern Time (UTC-5)</option>
                    <option value="UTC+0">GMT (UTC+0)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notifications Section */}
            <div className="settings-section" style={{ display: "none" }}>
              <h2>🔔 Notification Preferences</h2>
              <div className="settings-list">
                <div className="setting-toggle">
                  <div className="toggle-info">
                    <strong>Push Notifications</strong>
                    <p>Receive notifications about account activity</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications}
                      onChange={(e) =>
                        handleSettingChange("notifications", e.target.checked)
                      }
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-toggle">
                  <div className="toggle-info">
                    <strong>Email Updates</strong>
                    <p>Receive periodic updates via email</p>
                  </div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={settings.emailUpdates}
                      onChange={(e) =>
                        handleSettingChange("emailUpdates", e.target.checked)
                      }
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="settings-actions" style={{ display: "none" }}>
              <button className="btn save-btn" onClick={handleSaveSettings}>
                Save All Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Password Modal */}
      {user && (
        <ResetPassword
          isOpen={isResetPasswordOpen}
          user={{
            id: user.id,
            userName: user.username,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role || "",
            email: user.email || "",
            isActive: user.isActive || true,
          }}
          businessId="" // Not needed for self-reset
          onClose={handleCloseResetPassword}
          onSave={handlePasswordReset}
          isSelfReset={true}
        />
      )}
    </div>
  );
};

export default UserSettings;
